import { rankItem } from '@tanstack/match-sorter-utils';
import { ComplianceDataResponse, PieStatusKey } from '../types/dashboard.types';
import { FundStatusDateType, FundStatusWithDate } from '../dashboard-utils';
import { ChartDataPoint, FilteredChartRecord } from '../types/chart.types';

export function filterComplianceDataByRegulationId(
  fullData: ComplianceDataResponse,
  regulationId: string | undefined,
): ComplianceDataResponse {
  if (!regulationId) return fullData;

  // Get the specific regulation data from regulation_breakdown
  const regulationData = fullData.regulation_breakdown[regulationId];
  if (!regulationData) {
    // Return empty structure if regulation not found
    return {
      regulation_breakdown: {},
      manco_data: fullData.manco_data,
    };
  }

  // Return the full data structure but with only the specific regulation
  return {
    ...fullData,
    regulation_breakdown: {
      [regulationId]: regulationData,
    },
  };
}

// Utility functions
export const processChartEntry = (
  entry: ChartDataPoint,
  subCategoryFilters: string[],
  fundFilters: string[],
  searchTerm: string,
  statusFilters: PieStatusKey[],
) => {
  const filteredData: FilteredChartRecord = {};

  // Process each subcategory
  subCategoryFilters.forEach((key) => {
    const original = entry.data[key];
    if (!original) return;

    // Step 1: Apply fund filters
    let failFunds = applyFundFilter(original.Fail.funds, fundFilters);
    let alertFunds = applyFundFilter(original.Alert.funds, fundFilters);
    let passFunds = applyFundFilter(original.Pass.funds, fundFilters);

    // Step 2: Apply search filters
    failFunds = applySearchFilter(failFunds, searchTerm, key);
    alertFunds = applySearchFilter(alertFunds, searchTerm, key);
    passFunds = applySearchFilter(passFunds, searchTerm, key);

    // Step 3: Apply status filters
    const shouldIncludeFail = shouldIncludeStatus('Fail', statusFilters);
    const shouldIncludeAlert = shouldIncludeStatus('Alert', statusFilters);
    const shouldIncludePass = shouldIncludeStatus('Pass', statusFilters);

    const finalFailFunds = shouldIncludeFail ? failFunds : [];
    const finalAlertFunds = shouldIncludeAlert ? alertFunds : [];
    const finalPassFunds = shouldIncludePass ? passFunds : [];

    // Only add to filteredData if there's remaining data
    if (
      finalFailFunds.length > 0 ||
      finalAlertFunds.length > 0 ||
      finalPassFunds.length > 0
    ) {
      filteredData[key] = {
        Fail: {
          count: finalFailFunds.length,
          funds: finalFailFunds,
        },
        Alert: {
          count: finalAlertFunds.length,
          funds: finalAlertFunds,
        },
        Pass: {
          count: finalPassFunds.length,
          funds: finalPassFunds,
        },
      };
    }
  });

  // Create flat structure for chart rendering
  const flatCounts = createFlatCounts(filteredData);

  return {
    ...flatCounts,
    date: entry.date,
    data: filteredData, // For tooltips or hover info
  };
};

export const applyFundFilter = (
  funds: {
    fund_id: string;
    fund_name: string;
    fund_name_full: string;
  }[],
  fundFilters: string[],
) => {
  // If no fund filters are selected, show all funds (empty filters = show all)
  if (!fundFilters || fundFilters.length === 0) return funds;

  return funds.filter((fund) => fundFilters.includes(fund.fund_name));
};

export const applySearchFilter = (
  funds: {
    fund_id: string;
    fund_name: string;
    fund_name_full: string;
  }[],
  searchTerm: string,
  key: string,
) => {
  if (!searchTerm) return funds;

  return funds.filter((fund) => {
    // Check if key name matches the search term
    const keyMatch = key
      ? rankItem(key.toLowerCase(), searchTerm.toLowerCase(), {
          threshold: 3,
        }).passed
      : false;

    // Otherwise, fuzzy search in fund values
    const valueMatch = Object.values(fund).some((value) => {
      if (value === null || value === undefined) return false;
      const itemRank = rankItem(String(value), searchTerm, {
        threshold: 3,
      });
      return itemRank.passed;
    });

    return keyMatch || valueMatch;
  });
};

export const shouldIncludeStatus = (
  status: string,
  statusFilters: string[],
) => {
  return statusFilters.includes(status);
};

export const createFlatCounts = (filteredData: FilteredChartRecord) => {
  return Object.fromEntries(
    Object.entries(filteredData).flatMap(([key, val]) => [
      [`${key}_Fail`, val.Fail.count],
      [`${key}_Alert`, val.Alert.count],
      [`${key}_Pass`, val.Pass.count],
    ]),
  );
};

export function extractAllDates(
  data: Record<string, FundStatusWithDate[]>,
): string[] {
  const dateSet = new Set<string>();

  Object.values(data).forEach((entries) => {
    entries.forEach(({ date }) => {
      dateSet.add(date);
    });
  });

  return Array.from(dateSet).sort();
}

// New function to extract dates directly from ChartDataPoint[]
export function extractAllDatesFromChartData(
  chartData: ChartDataPoint[],
): string[] {
  return chartData.map((point) => point.date).sort();
}

// New function to process chart entries directly from ChartDataPoint[]
export const processChartEntryFromChartData = (
  entry: ChartDataPoint,
  subCategoryFilters: string[],
  fundFilters: string[],
  searchTerm: string,
  statusFilters: PieStatusKey[],
) => {
  const filteredData: FilteredChartRecord = {};

  // Process each subcategory - if no filters, process all subcategories
  const subcategoriesToProcess =
    subCategoryFilters.length > 0
      ? subCategoryFilters
      : Object.keys(entry.data);

  subcategoriesToProcess.forEach((key) => {
    const original = entry.data[key];
    if (!original) return;

    // Step 1: Apply fund filters
    let failFunds = applyFundFilter(original.Fail.funds, fundFilters);
    let alertFunds = applyFundFilter(original.Alert.funds, fundFilters);
    let passFunds = applyFundFilter(original.Pass.funds, fundFilters);

    // Step 2: Apply search filters
    failFunds = applySearchFilter(failFunds, searchTerm, key);
    alertFunds = applySearchFilter(alertFunds, searchTerm, key);
    passFunds = applySearchFilter(passFunds, searchTerm, key);

    // Step 3: Apply status filters
    const shouldIncludeFail = shouldIncludeStatus('Fail', statusFilters);
    const shouldIncludeAlert = shouldIncludeStatus('Alert', statusFilters);
    const shouldIncludePass = shouldIncludeStatus('Pass', statusFilters);

    const finalFailFunds = shouldIncludeFail ? failFunds : [];
    const finalAlertFunds = shouldIncludeAlert ? alertFunds : [];
    const finalPassFunds = shouldIncludePass ? passFunds : [];

    // Only add to filteredData if there's remaining data
    if (
      finalFailFunds.length > 0 ||
      finalAlertFunds.length > 0 ||
      finalPassFunds.length > 0
    ) {
      filteredData[key] = {
        Fail: {
          count: finalFailFunds.length,
          funds: finalFailFunds,
        },
        Alert: {
          count: finalAlertFunds.length,
          funds: finalAlertFunds,
        },
        Pass: {
          count: finalPassFunds.length,
          funds: finalPassFunds,
        },
      };
    }
  });

  // Create flat structure for chart rendering
  const flatCounts = createFlatCounts(filteredData);

  return {
    ...flatCounts,
    date: entry.date,
    data: filteredData, // For tooltips or hover info
  };
};

// This function generates the chart data based on the provided data and dates
export function generateChartData(
  data: Record<string, FundStatusWithDate[]>,
  dates: string[],
): { date: string; [subcategory: string]: number | string }[] {
  const subCategories = Object.keys(data);

  return dates.map((date) => {
    const row: { date: string; [subcategory: string]: number | string } = {
      date,
    };

    subCategories.forEach((subcategory) => {
      const entries = data[subcategory];
      const count = entries.filter((entry) => entry.date === date).length;
      row[subcategory] = count;
    });

    return row;
  });
}
